import {ReferringSpecialist} from '@/features/referring-specialists/types'
import {TeamGroup} from '@/features/treatment-requests/teams/types'
import i18n from '@/translations/i18n'
import {Contact, PatientAgeRange} from '@/types'
import {z} from 'zod'

export const ProjectType = z.enum(['cyclic', 'continuous'])
export type ProjectType = z.infer<typeof ProjectType>

export const ProjectTeamMember = z.object({
    id: z.string(),
    fullName: z.string(),
    specializationName: z.string(),
    isAReserve: z.boolean(),
    group: TeamGroup
})
export type ProjectTeamMember = z.infer<typeof ProjectTeamMember>

export const MedicalDevice = z.object({
    id: z.string(),
    device: z.enum([
        'respiratory_function_devices',
        'walking_aids',
        'abdominal_aids',
        'communication_aids',
        'personal_care_and_protection_devices',
        'trunk_orthoses',
        'postural_systems',
        'limb_devices_and_orthoses',
        'optical_and_acoustic_devices_and_prostheses',
        'other'
    ])
})
export type MedicalDevice = z.infer<typeof MedicalDevice>

export const ProjectCycleCount = z.enum(['one_cycle', 'two_cycles', 'single_non_repeatable_cycle'])
export type ProjectCycleCount = z.infer<typeof ProjectCycleCount>

export const AssistiveRegimeEnum = z.enum(['autism', 'adults', 'minors'])
export type AssistiveRegimeEnum = z.infer<typeof AssistiveRegimeEnum>

export const ProjectRehabCareCommitment = z.enum(['50', '60'])
export type ProjectRehabCareCommitment = z.infer<typeof ProjectRehabCareCommitment>

export const ProjectSender = z.enum(['pediatrician_doctor', 'hospital_specialist', 'district_services_specialist'])
export type ProjectSender = z.infer<typeof ProjectSender>

export const ProjectSiarRenewal = z.enum(['same_as_previous', 'empty'])
export type ProjectSiarRenewal = z.infer<typeof ProjectSiarRenewal>

export const ProjectStatusAction = z.enum(['suspended', 'reopen'])
export type ProjectStatusAction = z.infer<typeof ProjectStatusAction>

export const ExportSiarType = z.enum(['open_project', 'close_project'])
export type ExportSiarType = z.infer<typeof ExportSiarType>

export const PrintSiarSection = z.enum(['A', 'B', 'C', 'D', 'E', 'F'])
export type PrintSiarSection = z.infer<typeof PrintSiarSection>

export const PrintSiarCategorySection = z.enum(['open_project', 'close_project'])
export type PrintSiarCategorySection = z.infer<typeof PrintSiarCategorySection>

export const ProjectPatientLivingCondition = z.enum([
    'alone_or_with_partner',
    'with_partner_and_children',
    'with_other_family',
    'with_parents',
    'in_community_facility',
    'alone_with_personal_assistant',
    'alone'
])
export type ProjectPatientLivingCondition = z.infer<typeof ProjectPatientLivingCondition>

export const ProjectRehabilitationFacility = z.enum([
    'none',
    'hospital_facility',
    'extra_hospital_facility',
    'current_facility',
    'missing_info'
])
export type ProjectRehabilitationFacility = z.infer<typeof ProjectRehabilitationFacility>

export const ProjectEndingReason = z.enum([
    'full_recovery',
    'home_care',
    'remote_treatment',
    'outpatient_treatment',
    'semi_residential_facility',
    'residential_facility',
    'hospital_facility',
    'deceased',
    'proactive_health_programs',
    'residence_change',
    'abandonment',
    'other',
    'administrative_closure'
])
export type ProjectEndingReason = z.infer<typeof ProjectEndingReason>

export const ScaleType = z.enum([
    'lapmer',
    'barthel_index',
    'katz_index',
    'lawton_iadl',
    'mmse',
    'vas',
    'asia_impairment_scale',
    'wisci',
    'scim',
    'level_of_cognitive_functioning',
    'disability_rating_scale',
    'six_minutes_walking_test',
    'barthel_index_dyspnea',
    'glasgow_coma_scale',
    'glasgow_outcome_scale_extended',
    'modified_rankin_scale',
    'rehabilitation_complexity_scale',
    'pedi',
    'gmfcs',
    'gmfm',
    'tinetti',
    'vineland_ii',
    'wppsi_iv',
    'wisc_iv',
    'raven_matrices',
    'tower_of_london',
    'movement_abc_2',
    'sensory_disorders_indicator',
    'motor_disorders_gmfcs',
    'motor_disorders_macs',
    'motor_disorders_cfcs'
])
export type ScaleType = z.infer<typeof ScaleType>

export const ProjectSuspendReason = z.enum([
    'patient_medical_reasons',
    'patient_hospital_reasons',
    'patient_personal_reasons',
    'therapist_health_reasons'
])
export type ProjectSuspendReason = z.infer<typeof ProjectSuspendReason>

export const Diagnosis = z.object({
    id: z.string(),
    code: z.string(),
    description: z.string()
})
export type Diagnosis = z.infer<typeof Diagnosis>

export const ProjectPathology = z.object({
    id: z.string().min(1),
    diagnosisId: z.string().min(1),
    startedAt: z.string().min(1),
    pathology: Diagnosis
})
export type ProjectPathology = z.infer<typeof ProjectPathology>

export const BarthelScaleQuestionsPool = z
    .object({
        feeding: z.enum(['10', '8', '5', '2', '0']),
        bathing: z.enum(['5', '4', '3', '1', '0']),
        grooming: z.enum(['5', '4', '3', '1', '0']),
        dressing: z.enum(['10', '8', '5', '2', '0']),
        intenstinal_contincence: z.enum(['10', '8', '5', '2', '0']),
        urinary_contincence: z.enum(['10', '8', '5', '2', '0']),
        toilet_usage: z.enum(['10', '8', '5', '2', '0']),
        bed_weelchair_transfer: z.enum(['15', '12', '8', '3', '0']),
        mobility: z.enum(['15', '12', '8', '3', '0']),
        stairs: z.enum(['10', '8', '5', '2', '0']),
        weelchair_usage: z.enum(['5', '4', '3', '1', '0']).nullish()
    })
    .superRefine((data, ctx) => {
        if (data.mobility == '0' && data.weelchair_usage == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['weelchair_usage']
            })
        }
    })
export type BarthelScaleQuestionsPool = z.infer<typeof BarthelScaleQuestionsPool>

export const IadlScaleQuestionsPool = z.object({
    phone_usage: z.enum(['1', '2', '3', '4']),
    shopping: z.enum(['1', '2', '3', '4']),
    cooking: z.enum(['1', '2', '3', '4']),
    housekeeping: z.enum(['1', '2', '3', '4']),
    laundry: z.enum(['1', '2', '3']),
    transportation: z.enum(['1', '2', '3', '4', '5']),
    medication: z.enum(['1', '2', '3']),
    finance: z.enum(['1', '2', '3'])
})
export type IadlScaleQuestionsPool = z.infer<typeof IadlScaleQuestionsPool>

export const EvaluationScaleBase = z.object({
    evaluationDate: z.string().min(1).nullable(),
    type: ScaleType,
    totalScore: z.number().int().min(0).max(999)
})
export type EvaluationScaleBase = z.infer<typeof EvaluationScaleBase>

export const EvaluationScaleExtended = EvaluationScaleBase.and(
    z
        .object({type: z.literal(ScaleType.enum.barthel_index)})
        .merge(z.object({questions: BarthelScaleQuestionsPool.nullable()}))
        .or(
            z
                .object({type: z.literal(ScaleType.enum.lawton_iadl)})
                .merge(z.object({questions: IadlScaleQuestionsPool.nullable()}))
        )
        .or(z.object({type: ScaleType.exclude(['barthel_index', 'lawton_iadl'])}))
)
export type EvaluationScaleExtended = z.infer<typeof EvaluationScaleExtended>

export const DisabilityProfileLetter = z.enum(['b', 's', 'd', 'e'])
export type DisabilityProfileLetter = z.infer<typeof DisabilityProfileLetter>

export const DisabilityProfileIcfCode = z.enum([
    'b110',
    'b114',
    'b117',
    'b122',
    'b126',
    'b130',
    'b134',
    'b139',
    'b140',
    'b144',
    'b147',
    'b152',
    'b156',
    'b160',
    'b164',
    'b167',
    'b172',
    'b176',
    'b180',
    'b189',
    'b198',
    'b199',
    'b210',
    'b215',
    'b220',
    'b229',
    'b230',
    'b235',
    'b240',
    'b249',
    'b250',
    'b255',
    'b260',
    'b265',
    'b270',
    'b279',
    'b280',
    'b289',
    'b298',
    'b299',
    'b310',
    'b320',
    'b330',
    'b340',
    'b398',
    'b399',
    'b410',
    'b415',
    'b420',
    'b429',
    'b430',
    'b435',
    'b439',
    'b440',
    'b445',
    'b449',
    'b450',
    'b455',
    'b460',
    'b469',
    'b498',
    'b499',
    'b510',
    'b515',
    'b520',
    'b525',
    'b530',
    'b535',
    'b539',
    'b540',
    'b545',
    'b550',
    'b555',
    'b559',
    'b598',
    'b599',
    'b610',
    'b620',
    'b630',
    'b639',
    'b640',
    'b650',
    'b660',
    'b670',
    'b679',
    'b698',
    'b699',
    'b710',
    'b715',
    'b720',
    'b729',
    'b730',
    'b735',
    'b740',
    'b749',
    'b750',
    'b755',
    'b760',
    'b761',
    'b765',
    'b770',
    'b780',
    'b789',
    'b798',
    'b799',
    'b810',
    'b820',
    'b830',
    'b840',
    'b849',
    'b850',
    'b860',
    'b869',
    'b898',
    'b899',
    'd110',
    'd115',
    'd120',
    'd129',
    'd130',
    'd131',
    'd132',
    'd133',
    'd135',
    'd137',
    'd138',
    'd140',
    'd145',
    'd150',
    'd155',
    'd159',
    'd160',
    'd163',
    'd166',
    'd170',
    'd172',
    'd175',
    'd177',
    'd179',
    'd198',
    'd199',
    'd210',
    'd220',
    'd230',
    'd240',
    'd298',
    'd299',
    'd310',
    'd315',
    'd320',
    'd325',
    'd329',
    'd330',
    'd331',
    'd332',
    'd335',
    'd340',
    'd345',
    'd349',
    'd350',
    'd355',
    'd360',
    'd369',
    'd398',
    'd399',
    'd410',
    'd415',
    'd420',
    'd429',
    'd430',
    'd435',
    'd440',
    'd445',
    'd446',
    'd449',
    'd450',
    'd451',
    'd455',
    'd460',
    'd465',
    'd469',
    'd470',
    'd475',
    'd480',
    'd489',
    'd498',
    'd499',
    'd510',
    'd520',
    'd530',
    'd540',
    'd550',
    'd560',
    'd570',
    'd598',
    'd599',
    'd610',
    'd620',
    'd629',
    'd630',
    'd640',
    'd649',
    'd650',
    'd660',
    'd669',
    'd698',
    'd699',
    'd710',
    'd720',
    'd729',
    'd730',
    'd740',
    'd750',
    'd760',
    'd770',
    'd779',
    'd798',
    'd799',
    'd810',
    'd815',
    'd820',
    'd825',
    'd830',
    'd835',
    'd838',
    'd839',
    'd840',
    'd845',
    'd850',
    'd855',
    'd859',
    'd860',
    'd865',
    'd870',
    'd879',
    'd898',
    'd899',
    'd910',
    'd920',
    'd930',
    'd940',
    'd950',
    'd998',
    'd999',
    'e110',
    'e115',
    'e120',
    'e125',
    'e130',
    'e135',
    'e140',
    'e145',
    'e150',
    'e155',
    'e160',
    'e165',
    'e198',
    'e199',
    'e210',
    'e215',
    'e220',
    'e225',
    'e230',
    'e235',
    'e240',
    'e245',
    'e250',
    'e255',
    'e260',
    'e298',
    'e299',
    'e310',
    'e315',
    'e320',
    'e325',
    'e330',
    'e335',
    'e340',
    'e345',
    'e350',
    'e355',
    'e360',
    'e398',
    'e399',
    'e410',
    'e415',
    'e420',
    'e425',
    'e430',
    'e435',
    'e440',
    'e445',
    'e450',
    'e455',
    'e460',
    'e465',
    'e498',
    'e499',
    'e510',
    'e515',
    'e520',
    'e525',
    'e530',
    'e535',
    'e540',
    'e545',
    'e550',
    'e555',
    'e560',
    'e565',
    'e570',
    'e575',
    'e580',
    'e585',
    'e590',
    'e595',
    'e598',
    'e599',
    's110',
    's120',
    's130',
    's140',
    's150',
    's198',
    's199',
    's210',
    's220',
    's230',
    's240',
    's250',
    's260',
    's298',
    's299',
    's310',
    's320',
    's330',
    's340',
    's398',
    's399',
    's410',
    's420',
    's430',
    's498',
    's499',
    's510',
    's520',
    's530',
    's540',
    's550',
    's560',
    's570',
    's580',
    's598',
    's599',
    's610',
    's620',
    's630',
    's698',
    's699',
    's710',
    's720',
    's730',
    's740',
    's750',
    's760',
    's770',
    's798',
    's799',
    's810',
    's820',
    's830',
    's840',
    's898'
])
export type DisabilityProfileIcfCode = z.infer<typeof DisabilityProfileIcfCode>

export const DisabilityProfile = z.object({
    icfCode: z.object({code: DisabilityProfileIcfCode}),
    letter: DisabilityProfileLetter,
    type: z.enum(['barrier', 'helper']).nullable(),
    qualifier1: z.number().int(),
    qualifier2: z.number().int().nullable()
})
export type DisabilityProfile = z.infer<typeof DisabilityProfile>

export const ProjectStatus = z.enum(['open', 'closed', 'opening'])
export type ProjectStatus = z.infer<typeof ProjectStatus>

export const ProjectProgressStatus = z.enum(['incomplete', 'to_print', 'to_export', 'to_transmit', 'transmitted'])
export type ProjectProgressStatus = z.infer<typeof ProjectProgressStatus>

export const ProjectRefType = z.enum(['renewed', 'transformed', 'varied'])
export type ProjectRefType = z.infer<typeof ProjectRefType>

export const ProjectIndex = z.object({
    id: z.string().min(1),
    code: z.number(),
    isRenewed: z.boolean(),
    isVaried: z.boolean(),
    isTransformed: z.boolean(),
    type: z.enum(['cyclic', 'continuous']).nullable(),
    status: ProjectStatus.nullable(),
    startDate: z.string().min(1).nullable(),
    sector: z.enum(['outpatient', 'domiciliary', 'mixed', 'extended', 'maintenance']),
    durationDays: z.number().nullable(),
    expirationDate: z.string().nullable(),
    progressStatus: ProjectProgressStatus.nullable(),
    isSuspended: z.boolean().nullish(),
    endDate: z.string().min(1).nullable(),
    numberOfPlannedAccesses: z.number(),
    numberOfAccesses: z.number(),
    absenceDays: z.number(),
    suspensionDays: z.number(),
    openingExportedAt: z.string().min(1).nullable(),
    openingTransmissionAt: z.string().min(1).nullable(),
    closingExportedAt: z.string().min(1).nullable(),
    closingTransmissionAt: z.string().min(1).nullable(),
    renewedAt: z.string().nullable(),
    team: ProjectTeamMember.array(),
    patient: z.object({
        id: z.string().min(1),
        fullName: z.string().min(1),
        age: z.number().int(),
        birthDate: z.string().min(1),
        serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']),
        taxCode: z.string().min(1),
        contacts: z.array(Contact)
    }),
    referringDoctor: z
        .object({
            fullName: z.string().min(1),
            specializationName: z.string().min(1)
        })
        .nullable(),
    canDeleteProject: z.boolean()
})
export type ProjectIndex = z.infer<typeof ProjectIndex>

export const ProjectSmall = ProjectIndex.merge(
    z.object({
        patientId: z.string().min(1),
        treatmentRequestId: z.string().min(1).nullable(),
        authorizationDate: z.string().nullable(),
        sender: ProjectSender.nullable(),
        numberOfCycles: ProjectCycleCount.nullable(),
        evaluationScales: z.array(EvaluationScaleExtended),
        weeklyFrequencies: z.number().nullable(),
        createdAt: z.string(),
        conclusionReason: ProjectEndingReason.nullish(),
        updatedAt: z.string(),
        isRenewable: z.boolean(),
        specialist: ReferringSpecialist.nullable(),
        rehabilitationFacility: ProjectRehabilitationFacility.nullable(),
        livingConditions: ProjectPatientLivingCondition.nullable(),
        actualDays: z.coerce.number().nullable(),
        projectRefId: z.string().nullable(),
        projectRefType: ProjectRefType.nullable(),
        patient: z.object({
            contacts: z.array(Contact),
            taxCode: z.string().min(1),
            gender: z.enum(['male', 'female']),
            birthDate: z.string().min(1),
            fullName: z.string().min(1),
            age: z.number().int(),
            id: z.string().min(1),
            neuromuscularDiagnosisId: z.string().min(1).nullable(),
            domicileAslDistrict: z
                .object({
                    id: z.string().min(1),
                    name: z.string().min(1),
                    aslId: z.string().min(1),
                    aslName: z.string().min(1)
                })
                .nullable(),
            residenceAslDistrict: z
                .object({
                    id: z.string().min(1),
                    name: z.string().min(1),
                    aslId: z.string().min(1),
                    aslName: z.string().min(1)
                })
                .nullable(),
            residence: z.object({
                id: z.string().min(1),
                address: z.string().min(1),
                streetNumber: z.string().min(1),
                city: z.object({
                    name: z.string().min(1),
                    provinceAcronym: z.string().min(1)
                }),
                cap: z.string().min(1)
            }),
            domicile: z
                .object({
                    id: z.string().min(1),
                    address: z.string().min(1),
                    streetNumber: z.string().min(1),
                    city: z.object({
                        name: z.string().min(1),
                        provinceAcronym: z.string().min(1)
                    }),
                    cap: z.string().min(1)
                })
                .nullable(),
            serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']),
            patientId: z.string().min(1).nullish(), // this fileds are used for the snapshot
            treatmentRequestId: z.string().min(1).nullish() // this fileds are used for the snapshot
        }),
        assistiveRegime: AssistiveRegimeEnum.nullish(),
        renewedProjectId: z.string().nullable(),
        variedProjectId: z.string().nullable(),
        transformedProjectId: z.string().nullable(),
        isOpeningReportPresent: z.boolean(),
        isOpeningEventPresent: z.boolean(),
        isOpeningEventCompleted: z.boolean(),
        isTeamReportPresent: z.boolean(),
        isRenewContinuousProjectReportPresent: z.boolean(),
        isInitialProjectAssessmentReportPresent: z.boolean(),
        sirId: z.string().nullable()
    })
)
export type ProjectSmall = z.infer<typeof ProjectSmall>

export const ProjectExtended = ProjectSmall.merge(
    z.object({
        weeklyFrequency: z.coerce.number().nullish(),
        suspensionStartDate: z.date().nullish(),
        suspensionEndDate: z.date().nullish(),
        suspensionReason: ProjectSuspendReason.nullish(),
        missingGroups: z.array(TeamGroup),
        disabilityProfiles: z.array(DisabilityProfile),
        suspensions: z
            .array(
                z.object({
                    id: z.string(),
                    startDate: z.string(),
                    endDate: z.string().nullable(),
                    reason: ProjectSuspendReason,
                    notes: z.string().nullish()
                })
            )
            .optional()
    })
)
export type ProjectExtended = z.infer<typeof ProjectExtended>

export const ProjectByPatient = ProjectSmall.pick({
    id: true,
    createdAt: true,
    patientId: true,
    code: true,
    startDate: true,
    expirationDate: true,
    team: true,
    patient: true,
    endDate: true,
    status: true,
    type: true,
    sector: true,
    durationDays: true,
    isSuspended: true,
    treatmentRequestId: true
})
export type ProjectByPatient = z.infer<typeof ProjectByPatient>

export const ProjectDetailsUrlParams = z.object({id: z.string().min(1)})
export type ProjectDetailsUrlParams = z.infer<typeof ProjectDetailsUrlParams>

export const CreateProjectUrlParams = z.object({
    patientId: z.string().optional(),
    origin: z.enum(['treatment_request']).optional()
})

export interface Holiday {
    date: Date
    name: string
}

export type ProjectCreationError = 'already_exists' | 'tratment_request_error' | 'deceased'

export const BarthelScaleQuestion = z.enum([
    'feeding',
    'bathing',
    'grooming',
    'dressing',
    'intenstinal_contincence',
    'urinary_contincence',
    'toilet_usage',
    'bed_weelchair_transfer',
    'mobility',
    'stairs',
    'weelchair_usage'
])
export type BarthelScaleQuestion = z.infer<typeof BarthelScaleQuestion>

export const IadlScaleQuestion = z.enum([
    'phone_usage',
    'shopping',
    'cooking',
    'housekeeping',
    'laundry',
    'transportation',
    'medication',
    'finance'
])
export type IadlScaleQuestion = z.infer<typeof IadlScaleQuestion>

export const ProjectEditingOperation = z.enum(['renew', 'transform', 'variation', 'resume'])
export type ProjectEditingOperation = z.infer<typeof ProjectEditingOperation>

export const ProjectNotesType = z.enum(['administrative_activities', 'operational_notes', 'activity'])
export type ProjectNotesType = z.infer<typeof ProjectNotesType>

export const ProjectConclusionReason = z.enum(['renewal', 'transformation'])
export type ProjectConclusionReason = z.infer<typeof ProjectConclusionReason>

export const ParentalResponsibility = z.enum([
    'legal_guardian',
    'healthcare_facility',
    'missing_data',
    'shared',
    'maternal',
    'paternal'
])
export type ParentalResponsibility = z.infer<typeof ParentalResponsibility>

export const Applicant = z.enum([
    'hospital_acute_care_ward',
    'hospital_rehabilitation_department',
    'structure',
    'regional_services',
    'territorial_operations_center',
    'other',
    'ambulatory_specialist'
])
export type Applicant = z.infer<typeof Applicant>

export const NetworkSupport = z.enum(['presence', 'partial', 'non_presence'])
export type NetworkSupport = z.infer<typeof NetworkSupport>

export const ActivityAutonomy = z.enum(['autonomous', 'partially_dependent', 'totally_dependent'])
export type ActivityAutonomy = z.infer<typeof ActivityAutonomy>

export const MobilityAutonomy = z.enum([
    'moves_by_himself_protect_env',
    'moves_by_himself_not_protect_env',
    'moves_with_assistance_protect_env',
    'moves_with_assistance_not_protect_env',
    'does_not_move'
])
export type MobilityAutonomy = z.infer<typeof MobilityAutonomy>

export const ProjectClinicalPresentation = z.enum(['mild_nonexistent', 'moderate', 'severe'])
export type ProjectClinicalPresentation = z.infer<typeof ProjectClinicalPresentation>

export const StabilityLevel = z.enum([
    'evaluation_not_performed',
    'stable',
    'moderately_stable',
    'moderately_unstable',
    'unstable',
    'highly_unstable'
])
export type StabilityLevel = z.infer<typeof StabilityLevel>

export const ProjectClinicalCareNeed = z.enum([
    'sng_peg',
    'tracheotomy',
    'assisted_ventilation',
    'oxygen_therapy',
    'dialysis',
    'pressure_ulcers'
])
export type ProjectClinicalCareNeed = z.infer<typeof ProjectClinicalCareNeed>

export const ProjectAccessCounters = z.object({
    frequency: z.enum(['weekly', 'monthly']).nullish(),
    planned: z.coerce.number().int(),
    done: z.coerce.number().int(),
    scheduled: z.coerce.number().int(),
    toBeSchedule: z.coerce.number().int(),
    missed: z.coerce.number().int(),
    withdrawn: z.coerce.number().int()
})
export type ProjectAccessCounters = z.infer<typeof ProjectAccessCounters>

export const ProjectBaseColumn = z.enum([
    'fullName',
    'sector',
    'serviceType',
    'birthDate',
    'age',
    'type',
    'code',
    'startDate',
    'durationDays',
    'expirationDate',
    'status',
    'progressStatus',
    'isSuspended',
    'endDate',
    'completedAccessesPerCent',
    'absenceDays',
    'suspensionDays',
    'openingExportedAt',
    'openingTransmissionAt',
    'closingExportedAt',
    'closingTransmissionAt',
    'referringDoctorId',
    'telephone',
    'taxCode',
    'isRenewed'
])
export type ProjectBaseColumn = z.infer<typeof ProjectBaseColumn>

export const PresetProjectColumns = z.enum([
    'code',
    'startDate',
    'expirationDate',
    'endDate',
    'type',
    'sector',
    'assistiveRegime',
    'residenceAsl',
    'residenceAslDistrict',
    'domicileAsl',
    'domicileAslDistrict',
    'equipe',
    'plannedWeeklyTreatments',
    'plannedMonthlyTreatments',
    'otherDiagnosisCode'
])
export type PresetProjectColumns = z.infer<typeof PresetProjectColumns>

export const ProjectsColumn = z.enum([
    ...ProjectBaseColumn.options,
    ...PresetProjectColumns.options,
    'select',
    'moreActions'
])
export type ProjectsColumn = z.infer<typeof ProjectsColumn>
